import React, { useState } from 'react';
import { ArrowLeft, Download, Send, Calendar } from 'lucide-react';
import axios from 'axios';
import { useTheme } from '../../context/ThemeContext';
import { formatPrice, formatNumber } from '../../utils/formatters';

export default function ReportsSection({ onBack }) {
  const { colors } = useTheme();
    const today = new Date().toISOString().slice(0, 10);
    const monthAgo = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);

    const [fromDate, setFromDate] = useState(monthAgo);
    const [toDate, setToDate]     = useState(today);
    const [report, setReport]     = useState(null);
    const [loading, setLoading]   = useState(false);
    const [sending, setSending]   = useState(false);
    const [wordLoading, setWordLoading] = useState(false);
    const [expandedSection, setExpandedSection] = useState('summary');

    const API = process.env.REACT_APP_BACKEND_URL + '/api';

    const generate = async () => {
      setLoading(true);
      setReport(null);
      try {
        const [reportRes, scoreRes, behaviourRes] = await Promise.allSettled([
          axios.get(`${API}/reports/generate`, { params: { from_date: fromDate, to_date: toDate } }),
          axios.get(`${API}/discipline/score`),
          axios.get(`${API}/discipline/behaviour`),
        ]);
        const data = reportRes.status === 'fulfilled' ? reportRes.value.data : null;
        if (!data) throw new Error('report failed');
        if (scoreRes.status === 'fulfilled') {
          data.discipline = { ...data.discipline, ...scoreRes.value.data };
        }
        if (behaviourRes.status === 'fulfilled') {
          data.discipline_behaviour = behaviourRes.value.data;
        }
        setReport(data);
        setExpandedSection('summary');
      } catch (e) {
        import('react-hot-toast').then(m => m.default.error('Failed to generate report'));
      } finally { setLoading(false); }
    };

    const downloadWord = async () => {
      setWordLoading(true);
      try {
        const res = await axios.get(`${API}/reports/download/word`, {
          params: { from_date: fromDate, to_date: toDate },
          responseType: 'blob',
        });
        const url = URL.createObjectURL(res.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = `TradeReflex_Report_${fromDate}_to_${toDate}.docx`;
        a.click();
        URL.revokeObjectURL(url);
      } catch { import('react-hot-toast').then(m => m.default.error('Word download failed')); }
      finally { setWordLoading(false); }
    };

    const generateReportHTML = (r) => {
      const fmt2 = (n) => formatNumber(n || 0);
      const pc = (v) => Number(v) >= 0 ? '#10b981' : '#f43f5e';
      const sc = (v) => Number(v) >= 0 ? 'profit' : 'loss';
      const s = r.summary;
      const a = r.analytics || {};
      const d = r.discipline || {};
      const beh = r.discipline_behaviour || {};

      const statCard = (label, value, color) =>
        `<div class="stat-card"><p class="stat-label">${label}</p><p class="stat-value" style="color:${color}">${value}</p></div>`;

      const tableRow = (cells, isHeader) =>
        `<tr>${cells.map(c => isHeader ? `<th>${c}</th>` : `<td>${c}</td>`).join('')}</tr>`;

      const discComponents = d.components ? Object.entries(d.components).map(([k, v]) => {
        const labels = { stop_loss:'Stop Loss', risk_reward:'Risk-Reward', strategy:'Strategy', emotion:'Emotion', frequency:'Frequency', confidence:'Confidence', journal:'Journal', sl_violation:'SL Violations', risk_profile:'Risk Profile' };
        const color = v >= 70 ? '#10b981' : v >= 50 ? '#f59e0b' : '#f43f5e';
        return `<tr><td>${labels[k] || k}</td><td><div style="background:rgba(255,255,255,.08);border-radius:4px;height:7px;width:100%;"><div style="background:${color};height:7px;border-radius:4px;width:${v}%;"></div></div></td><td style="text-align:right;font-weight:700;color:${color}">${v}%</td></tr>`;
      }).join('') : '';

      const ordersRows = r.orders.map(o => {
        const price = o.executed_price || o.limit_price || 0;
        const src = o.source === 'NYX_AI' ? 'Nyx AI' : o.source === 'ALGO_FLOW' ? 'Algo' : 'Manual';
        return tableRow([(o.symbol||'').replace('.NS',''), o.order_type, o.order_variant, o.quantity, `Rs.${fmt2(price)}`, o.status, src], false);
      }).join('');

      const tradesRows = r.closed_trades.map(t => {
        const pnl  = Number(t.pnl||0);
        const disc = t.discipline_snap || {};
        const mkt  = t.market_context_snap || {};
        const discScore = disc.discipline_score ?? disc.score ?? null;
        const discCell  = discScore != null ? `<span style="color:${discScore>=70?'#10b981':discScore>=50?'#f59e0b':'#f43f5e'};font-weight:700">${discScore}/100</span>` : '—';
        const rCell     = t.actual_r != null ? `<span style="color:${Number(t.actual_r)>=1?'#10b981':'#f43f5e'};font-weight:700">${Number(t.actual_r).toFixed(2)}R</span>` : '—';
        const regimeCell = mkt.regime ? `<span style="font-size:10px;background:rgba(99,102,241,.2);color:#a5b4fc;padding:1px 6px;border-radius:3px;border:1px solid rgba(99,102,241,.25)">${mkt.regime}</span>` : '';
        const aiCell = t.ai_review ? `<span style="font-size:9px;color:#6b7280;font-style:italic">${t.ai_review.slice(0,120)}${t.ai_review.length>120?'…':''}</span>` : '';
        return `<tr>
          <td><strong>${(t.symbol||'').replace('.NS','')}</strong>${regimeCell ? '<br>'+regimeCell : ''}</td>
          <td>${t.product_type||''}</td>
          <td>Rs.${fmt2(t.entry_price)}</td>
          <td>Rs.${fmt2(t.exit_price)}</td>
          <td class="${sc(pnl)}" style="font-weight:700">${pnl>=0?'+':''}Rs.${fmt2(pnl)}<br><small style="opacity:.75">${Number(t.pnl_pct||0).toFixed(2)}%</small></td>
          <td>${rCell}</td>
          <td>${discCell}</td>
          <td>${t.exit_reason||''}</td>
          ${aiCell ? `<td style="max-width:160px">${aiCell}</td>` : '<td>—</td>'}
        </tr>`;
      }).join('');

      const ptRows = Object.entries(r.by_product_type||{}).map(([pt, v]) => {
        const wr = v.trades ? Math.round(v.wins/v.trades*100) : 0;
        return `<tr><td><strong>${pt}</strong></td><td>${v.trades}</td><td class="${sc(v.pnl)}">${v.pnl>=0?'+':''}Rs.${fmt2(v.pnl)}</td><td>${wr}%</td></tr>`;
      }).join('');

      const srcRows = Object.entries(r.by_source||{}).map(([src, cnt]) => {
        const label = src === 'NYX_AI' ? 'Nyx AI' : src === 'ALGO_FLOW' ? 'Algo Flow' : 'Manual';
        return `<tr><td>${label}</td><td>${cnt}</td></tr>`;
      }).join('');

      const topRows = (r.top_performers||[]).map(p =>
        `<tr><td><strong style="color:#e2e8f0">${p.symbol}</strong></td><td>${p.trades}</td><td class="profit" style="font-weight:700">+Rs.${fmt2(p.pnl)}</td></tr>`).join('');
      const botRows = (r.bottom_performers||[]).map(p =>
        `<tr><td><strong style="color:#e2e8f0">${p.symbol}</strong></td><td>${p.trades}</td><td class="loss" style="font-weight:700">Rs.${fmt2(p.pnl)}</td></tr>`).join('');

      const streakText = a.current_streak > 0
        ? `<span style="color:#10b981;font-weight:700">+${a.current_streak} win streak</span>`
        : a.current_streak < 0
        ? `<span style="color:#f43f5e;font-weight:700">${Math.abs(a.current_streak)} loss streak</span>`
        : '<span style="color:#6b7280">No current streak</span>';

      return `<!DOCTYPE html><html><head>
        <meta charset="utf-8"/>
        <title>TradeReflex Report ${r.meta.from_date} to ${r.meta.to_date}</title>
        <style>
          *{box-sizing:border-box;margin:0;padding:0}
          body{font-family:'Segoe UI',Arial,sans-serif;background:#0d0e14;color:#e2e8f0;font-size:12px;padding:24px}
          .header{background:linear-gradient(135deg,#1e1b4b 0%,#312e81 40%,#4c1d95 100%);border:1px solid rgba(139,92,246,.3);color:#fff;padding:28px 32px;border-radius:16px;margin-bottom:24px;text-align:center;position:relative;overflow:hidden}
          .header::before{content:'';position:absolute;top:-50%;left:-50%;width:200%;height:200%;background:radial-gradient(circle at 60% 40%,rgba(139,92,246,.15) 0%,transparent 60%);pointer-events:none}
          .header h1{font-size:26px;font-weight:800;letter-spacing:-0.5px;margin-bottom:6px;background:linear-gradient(90deg,#c4b5fd,#fff,#a5b4fc);-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text}
          .header .meta{font-size:11px;color:rgba(196,181,253,.8);margin-top:2px}
          .header .trader{display:inline-block;margin-top:10px;background:rgba(139,92,246,.25);border:1px solid rgba(139,92,246,.4);border-radius:999px;padding:3px 14px;font-size:11px;color:#c4b5fd;font-weight:600}
          h2{font-size:12px;font-weight:700;margin:22px 0 10px;padding:6px 12px;background:rgba(99,102,241,.12);border-left:3px solid #6366f1;border-radius:0 6px 6px 0;color:#a5b4fc;text-transform:uppercase;letter-spacing:.8px}
          .grid-3{display:grid;grid-template-columns:repeat(3,1fr);gap:10px;margin-bottom:16px}
          .grid-6{display:grid;grid-template-columns:repeat(6,1fr);gap:8px;margin-bottom:16px}
          .stat-card{background:linear-gradient(135deg,rgba(30,27,75,.8),rgba(17,24,39,.9));border:1px solid rgba(99,102,241,.2);border-radius:10px;padding:12px 8px;text-align:center;position:relative;overflow:hidden}
          .stat-card::before{content:'';position:absolute;top:0;left:0;right:0;height:2px;background:linear-gradient(90deg,#6366f1,#8b5cf6)}
          .stat-label{font-size:8px;color:#6b7280;text-transform:uppercase;letter-spacing:.8px;margin-bottom:5px}
          .stat-value{font-size:17px;font-weight:800}
          table{width:100%;border-collapse:collapse;margin-bottom:16px;font-size:11px}
          th{background:rgba(15,16,22,.8);border-bottom:1px solid rgba(99,102,241,.25);padding:7px 10px;text-align:left;font-size:9px;text-transform:uppercase;letter-spacing:.6px;font-weight:700;color:#6366f1}
          td{padding:6px 10px;border-bottom:1px solid rgba(255,255,255,.05);vertical-align:middle;color:#cbd5e1}
          tr:nth-child(even) td{background:rgba(255,255,255,.02)}
          .profit{color:#10b981} .loss{color:#f43f5e}
          .badge{display:inline-block;padding:2px 8px;border-radius:999px;font-size:9px;font-weight:700;background:rgba(99,102,241,.2);border:1px solid rgba(99,102,241,.3);color:#a5b4fc}
          .two-col{display:grid;grid-template-columns:1fr 1fr;gap:14px}
          .box{background:rgba(17,24,39,.6);border:1px solid rgba(255,255,255,.08);border-radius:10px;padding:14px}
          .best-box{background:linear-gradient(135deg,rgba(6,78,59,.4),rgba(17,24,39,.8));border:1px solid rgba(16,185,129,.25);border-radius:10px;padding:14px}
          .worst-box{background:linear-gradient(135deg,rgba(76,5,25,.4),rgba(17,24,39,.8));border:1px solid rgba(244,63,94,.25);border-radius:10px;padding:14px}
          .disc-bar-row td{padding:4px 10px}
          .disc-track{background:rgba(255,255,255,.08);border-radius:4px;height:7px;width:100%}
          .section-card{background:rgba(13,14,20,.6);border:1px solid rgba(99,102,241,.15);border-radius:12px;padding:16px;margin-bottom:16px}
          .footer{text-align:center;color:#374151;font-size:9px;margin-top:28px;padding-top:14px;border-top:1px solid rgba(255,255,255,.06)}
          .tag-chip{display:inline-block;background:rgba(99,102,241,.15);border:1px solid rgba(99,102,241,.25);color:#818cf8;padding:1px 6px;border-radius:3px;font-size:9px}
          @media print{body{background:#0d0e14;padding:10px} @page{margin:12mm;background:#0d0e14} .header{border-radius:8px}}
        </style>
      </head><body>
        <div class="header">
          <h1>TradeReflex — Trade Report</h1>
          <p class="meta">${r.meta.from_date} &nbsp;&mdash;&nbsp; ${r.meta.to_date} &nbsp;|&nbsp; Generated ${r.meta.generated_at.slice(0,19).replace('T',' ')} IST</p>
          ${r.meta.username ? `<span class="trader">Trader: ${r.meta.username}</span>` : ''}
        </div>

        <h2>Summary</h2>
        <div class="grid-6">
          ${statCard('Total Orders', s.total_orders, '#94a3b8')}
          ${statCard('Executed', s.executed_orders, '#10b981')}
          ${statCard('Cancelled', s.cancelled_orders, '#f43f5e')}
          ${statCard('Closed Trades', s.closed_trades, '#94a3b8')}
          ${statCard('Win Rate', s.win_rate+'%', s.win_rate>=50?'#10b981':'#f43f5e')}
          ${statCard('Total P&L', 'Rs.'+fmt2(s.total_pnl), pc(s.total_pnl))}
        </div>
        <div class="two-col" style="margin-bottom:18px">
          ${s.best_trade ? `<div class="best-box"><p style="font-size:8px;font-weight:700;color:#10b981;text-transform:uppercase;letter-spacing:.6px;margin-bottom:6px">Best Trade</p><p style="font-weight:700;font-size:14px;color:#e2e8f0">${(s.best_trade.symbol||'').replace('.NS','')}</p><p style="color:#10b981;font-size:15px;font-weight:800;margin-top:3px">+Rs.${fmt2(s.best_trade.pnl)} <span style="font-size:11px;font-weight:600">(${Number(s.best_trade.pnl_pct||0).toFixed(2)}%)</span></p></div>` : '<div></div>'}
          ${s.worst_trade ? `<div class="worst-box"><p style="font-size:8px;font-weight:700;color:#f43f5e;text-transform:uppercase;letter-spacing:.6px;margin-bottom:6px">Worst Trade</p><p style="font-weight:700;font-size:14px;color:#e2e8f0">${(s.worst_trade.symbol||'').replace('.NS','')}</p><p style="color:#f43f5e;font-size:15px;font-weight:800;margin-top:3px">Rs.${fmt2(s.worst_trade.pnl)} <span style="font-size:11px;font-weight:600">(${Number(s.worst_trade.pnl_pct||0).toFixed(2)}%)</span></p></div>` : '<div></div>'}
        </div>

        <h2>Risk &amp; Quality Analytics</h2>
        <div class="grid-3" style="margin-bottom:10px">
          ${statCard('Avg R:R Ratio', a.avg_rr ? '1:'+a.avg_rr : 'N/A', a.avg_rr>=2?'#10b981':a.avg_rr>=1?'#f59e0b':'#f43f5e')}
          ${statCard('SL Usage', a.sl_usage_pct+'%', a.sl_usage_pct>=80?'#10b981':'#f59e0b')}
          ${statCard('Target Set', a.target_usage_pct+'%', a.target_usage_pct>=60?'#10b981':'#f59e0b')}
          ${statCard('Max Win Streak', a.max_win_streak, '#10b981')}
          ${statCard('Max Loss Streak', a.max_loss_streak, '#f43f5e')}
          ${statCard('Avg Hold', a.avg_hold_hours>24?Math.round(a.avg_hold_hours/24)+'d':a.avg_hold_hours+'h', '#94a3b8')}
        </div>
        <table><tr><th>Metric</th><th>Value</th></tr>
          <tr><td>Current Streak</td><td>${streakText}</td></tr>
          <tr><td>Max Drawdown</td><td class="loss">Rs.${fmt2(a.max_drawdown)}</td></tr>
          <tr><td>Orders with Stop Loss</td><td>${a.orders_with_sl} / ${s.total_orders}</td></tr>
          <tr><td>Orders with Target</td><td>${a.orders_with_target} / ${s.total_orders}</td></tr>
        </table>

        ${d.score != null ? `
        <h2>Discipline Score &mdash; ${d.score}/100</h2>
        <div class="section-card" style="margin-bottom:16px">
          <div style="background:rgba(255,255,255,.08);border-radius:6px;height:10px;margin-bottom:12px;overflow:hidden">
            <div style="background:linear-gradient(90deg,${d.score>=70?'#10b981':d.score>=50?'#f59e0b':'#f43f5e'},${d.score>=70?'#34d399':d.score>=50?'#fcd34d':'#fb7185'});height:10px;border-radius:6px;width:${d.score}%"></div>
          </div>
          <table><tr><th>Component</th><th>Score Bar</th><th style="text-align:right">Score</th></tr>
          ${discComponents}
          </table>
        </div>` : ''}

        ${(() => {
          const streak = beh.streak || {};
          const tod = beh.time_of_day || {};
          const conf = beh.confidence_calibration || {};
          const regimes = beh.regime_discipline || [];
          const inr = n => n != null ? `Rs.${formatNumber(Math.round(n))}` : '—';
          const streakCount = streak.losing_streak || 0;
          const streakColor = streakCount >= 3 ? '#f43f5e' : streakCount >= 2 ? '#f59e0b' : '#10b981';

          const todRows = (tod.by_hour || []).map(h => {
            const c = h.avg_pnl >= 0 ? '#10b981' : '#f43f5e';
            const isBest  = tod.best_hour  && h.hour === tod.best_hour.hour;
            const isWorst = tod.worst_hour && h.hour === tod.worst_hour.hour;
            const badge = isBest ? '<span style="background:#10b98120;color:#10b981;padding:1px 6px;border-radius:3px;font-size:9px;margin-left:4px">Best</span>' : isWorst ? '<span style="background:#f43f5e20;color:#f43f5e;padding:1px 6px;border-radius:3px;font-size:9px;margin-left:4px">Worst</span>' : '';
            return `<tr><td>${h.label}${badge}</td><td>${h.cnt} trades</td><td class="${h.avg_pnl>=0?'profit':'loss'}" style="font-weight:700;color:${c}">${h.avg_pnl>=0?'+':''}${inr(h.avg_pnl)}</td></tr>`;
          }).join('');

          const confRows = (conf.tiers || []).map(t => {
            const color = t.win_rate >= 60 ? '#10b981' : t.win_rate >= 40 ? '#f59e0b' : '#f43f5e';
            return `<tr><td>${t.label}</td><td>${t.count} trades</td><td style="color:${color};font-weight:700">${t.win_rate}%</td><td style="color:${t.avg_pnl>=0?'#10b981':'#f43f5e'};font-weight:700">${t.avg_pnl>=0?'+':''}${inr(t.avg_pnl)}</td></tr>`;
          }).join('');

          const regimeRows = regimes.map(r => {
            const color = r.avg_score >= 65 ? '#10b981' : r.avg_score >= 45 ? '#f59e0b' : '#f43f5e';
            return `<tr><td>${r.regime}</td><td>${r.days} sessions</td><td style="color:${color};font-weight:700">${Math.round(r.avg_score)}</td><td style="color:${r.total_pnl>=0?'#10b981':'#f43f5e'};font-weight:700">${r.total_pnl>=0?'+':''}${inr(r.total_pnl)}</td></tr>`;
          }).join('');

          return `
        <h2>Behaviour &amp; Pattern Analysis</h2>

        <div class="two-col" style="margin-bottom:16px">
          <div class="section-card">
            <p style="font-size:9px;font-weight:700;color:#94a3b8;text-transform:uppercase;letter-spacing:.6px;margin-bottom:8px">Losing Streak</p>
            <p style="font-size:28px;font-weight:800;color:${streakColor}">${streakCount} <span style="font-size:13px;font-weight:500">day${streakCount !== 1 ? 's' : ''}</span></p>
            ${streak.warning ? `<p style="font-size:10px;color:${streakColor};margin-top:6px;line-height:1.4">${streak.warning}</p>` : '<p style="font-size:10px;color:#10b981;margin-top:6px">No losing streak — good form!</p>'}
          </div>
          <div class="section-card">
            <p style="font-size:9px;font-weight:700;color:#94a3b8;text-transform:uppercase;letter-spacing:.6px;margin-bottom:8px">Best vs Worst Hour</p>
            ${tod.best_hour  ? `<p style="font-size:11px;margin-bottom:4px"><span style="color:#10b981;font-weight:700">${tod.best_hour.label}</span> <span style="color:#6b7280">best avg</span> <span style="color:#10b981;font-weight:700">+${inr(tod.best_hour.avg_pnl)}</span></p>` : ''}
            ${tod.worst_hour ? `<p style="font-size:11px"><span style="color:#f43f5e;font-weight:700">${tod.worst_hour.label}</span> <span style="color:#6b7280">worst avg</span> <span style="color:#f43f5e;font-weight:700">${inr(tod.worst_hour.avg_pnl)}</span></p>` : ''}
            ${!tod.best_hour && !tod.worst_hour ? '<p style="font-size:10px;color:#6b7280">No time-of-day data yet</p>' : ''}
          </div>
        </div>

        ${todRows ? `
        <p style="font-size:9px;font-weight:700;color:#94a3b8;text-transform:uppercase;letter-spacing:.6px;margin-bottom:8px">P&amp;L by Hour of Day (IST)</p>
        <table><tr><th>Hour</th><th>Trades</th><th>Avg P&amp;L</th></tr>${todRows}</table>` : ''}

        ${confRows ? `
        <p style="font-size:9px;font-weight:700;color:#94a3b8;text-transform:uppercase;letter-spacing:.6px;margin:16px 0 8px">Confidence vs Outcome</p>
        <table><tr><th>Confidence Level</th><th>Trades</th><th>Win Rate</th><th>Avg P&amp;L</th></tr>${confRows}</table>
        ${conf.insight ? `<p style="font-size:10px;color:#94a3b8;font-style:italic;margin-top:6px">${conf.insight}</p>` : ''}` : ''}

        ${regimeRows ? `
        <p style="font-size:9px;font-weight:700;color:#94a3b8;text-transform:uppercase;letter-spacing:.6px;margin:16px 0 8px">Discipline by Market Regime</p>
        <table><tr><th>Regime</th><th>Sessions</th><th>Avg Disc. Score</th><th>Total P&amp;L</th></tr>${regimeRows}</table>` : ''}
          `;
        })()}

        <h2>Intraday vs Swing</h2>
        <table><tr><th>Type</th><th>Trades</th><th>Total P&L</th><th>Win Rate</th></tr>${ptRows}</table>

        <h2>Orders by Source</h2>
        <table><tr><th>Source</th><th>Orders</th></tr>${srcRows}</table>

        ${(r.top_performers||[]).length > 0 ? `
        <h2>Top &amp; Bottom Performers</h2>
        <div class="two-col">
          <div><p style="font-size:9px;font-weight:700;color:#10b981;margin-bottom:8px;text-transform:uppercase;letter-spacing:.6px">Top Stocks</p>
          <table><tr><th>Symbol</th><th>Trades</th><th>P&L</th></tr>${topRows}</table></div>
          <div><p style="font-size:9px;font-weight:700;color:#f43f5e;margin-bottom:8px;text-transform:uppercase;letter-spacing:.6px">Bottom Stocks</p>
          <table><tr><th>Symbol</th><th>Trades</th><th>P&L</th></tr>${botRows}</table></div>
        </div>` : ''}

        ${Object.keys(r.by_strategy||{}).length > 0 ? `
        <h2>Strategy Breakdown</h2>
        <table><tr><th>Strategy Tag</th><th>Trades</th><th>Wins</th><th>P&L</th></tr>
        ${Object.entries(r.by_strategy).sort((a,b)=>b[1].trades-a[1].trades).map(([tag,v])=>
          `<tr><td><span class="badge">${tag}</span></td><td>${v.trades}</td><td style="color:#10b981">${v.wins||0}</td><td class="${sc(v.pnl)}" style="font-weight:700">${v.pnl>=0?'+':''}Rs.${fmt2(v.pnl)}</td></tr>`
        ).join('')}
        </table>` : ''}

        <h2>All Orders (${r.orders.length})</h2>
        <table><tr><th>Symbol</th><th>Type</th><th>Variant</th><th>Qty</th><th>Price</th><th>Status</th><th>Source</th></tr>
        ${ordersRows}</table>

        ${r.closed_trades.length > 0 ? `
        <h2>Closed Trades (${r.closed_trades.length})</h2>
        <table><tr><th>Symbol</th><th>Type</th><th>Entry</th><th>Exit</th><th>P&L</th><th>Actual R</th><th>Discipline</th><th>Exit Reason</th><th>AI Review</th></tr>
        ${tradesRows}</table>` : ''}

        <div class="footer">TradeReflex Paper Trading Platform &nbsp;&bull;&nbsp; Generated ${r.meta.generated_at.slice(0,19).replace('T',' ')} IST &nbsp;&bull;&nbsp; Not financial advice</div>
      </body></html>`;
    };

    const downloadPDF = () => {
      if (!report) return;
      const html = generateReportHTML(report);
      const blob = new Blob([html], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const win = window.open(url, '_blank');
      if (!win) { import('react-hot-toast').then(m => m.default.error('Pop-up blocked — allow pop-ups for this site')); return; }
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    };

    const sendEmail = async () => {
      if (!report) { import('react-hot-toast').then(m => m.default.error('Generate a report first')); return; }
      setSending(true);
      try {
        const pdf_html = generateReportHTML(report);
        const res = await axios.post(`${API}/reports/email`, { from_date: fromDate, to_date: toDate, pdf_html });
        import('react-hot-toast').then(m => m.default.success(res.data?.message || 'Report sent'));
      } catch (e) {
        const msg = e?.response?.data?.detail || 'Failed to send email';
        import('react-hot-toast').then(m => m.default.error(msg));
      } finally { setSending(false); }
    };

    const pnlColor = (v) => Number(v) >= 0 ? '#16a34a' : '#dc2626';
    const statusColor = (s) => s === 'EXECUTED' || s === 'CLOSED' ? '#16a34a' : s === 'CANCELLED' ? '#ef4444' : '#f59e0b';
    const srcLabel = (s) => s === 'NYX_AI' ? '✦ Nyx AI' : s === 'ALGO_FLOW' ? '⚙ Algo' : 'Manual';
    const srcColor = (s) => s === 'NYX_AI' ? '#818cf8' : s === 'ALGO_FLOW' ? '#f59e0b' : colors.secondaryText;

    const AccordionSection = ({ id, title, children }) => (
      <div className="rounded-xl overflow-hidden border mb-2" style={{ borderColor: colors.border }}>
        <button className="app-card w-full flex items-center justify-between px-1.5 py-1"
          style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)` }}
          onClick={() => setExpandedSection(expandedSection === id ? null : id)}>
          <span className="text-[14px] font-semibold" style={{ color: colors.primaryText }}>{title}</span>
          <span className="text-[13px]" style={{ color: colors.secondaryText }}>{expandedSection === id ? '▲' : '▼'}</span>
        </button>
        {expandedSection === id && (
          <div className="px-1.5 pb-1 pt-1" style={{ backgroundColor: colors.background }}>
            {children}
          </div>
        )}
      </div>
    );

    return (
      <div className="space-y-3">
        {/* Header */}
        <div className="flex items-center gap-3">
          <button onClick={() => onBack()} className="app-card p-1.5 rounded-md" style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)` }}>
            <ArrowLeft className="w-4 h-4" style={{ color: colors.primaryText }} />
          </button>
          <div>
            <h2 className="text-[14px] font-bold" style={{ color: colors.primaryText }}>Trade Reports</h2>
            <p className="text-[13px]" style={{ color: colors.secondaryText }}>Select a date range and generate your analysis</p>
          </div>
        </div>

        {/* Date picker + Generate */}
        <div className="app-card rounded-xl p-1.5 space-y-3" style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)`, border: `1px solid ${colors.border}` }}>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[13px] font-semibold block mb-1" style={{ color: colors.secondaryText }}>FROM</label>
              <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
                className="w-full px-3 py-1 rounded-lg text-[14px]"
                style={{ backgroundColor: colors.inputBg, color: colors.primaryText, border: `1px solid ${colors.border}` }} />
            </div>
            <div>
              <label className="text-[13px] font-semibold block mb-1" style={{ color: colors.secondaryText }}>TO</label>
              <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
                className="w-full px-3 py-1 rounded-lg text-[14px]"
                style={{ backgroundColor: colors.inputBg, color: colors.primaryText, border: `1px solid ${colors.border}` }} />
            </div>
          </div>

          {/* Quick presets */}
          <div className="flex gap-2 flex-wrap">
            {[
              { label: 'Today', days: 0 },
              { label: 'Last 7d', days: 7 },
              { label: 'Last 30d', days: 30 },
              { label: 'Last 90d', days: 90 },
            ].map(({ label, days }) => (
              <button key={label}
                className="text-[13px] px-3 py-1 rounded-full font-medium"
                style={{ backgroundColor: colors.inputBg, color: colors.accent }}
                onClick={() => {
                  const t = new Date();
                  const f = new Date(Date.now() - days * 86400000);
                  setToDate(t.toISOString().slice(0, 10));
                  setFromDate(f.toISOString().slice(0, 10));
                }}>
                {label}
              </button>
            ))}
          </div>

          <button onClick={generate} disabled={loading}
            className="w-full py-1 rounded-xl font-semibold text-[14px] text-white disabled:opacity-50"
            style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)' }}>
            {loading ? 'Generating…' : '✦ Generate Report'}
          </button>
        </div>

        {/* Report content */}
        {report && (
          <>
            {/* Download / Email actions */}
            <div className="app-card rounded-xl p-1.5 space-y-2.5" style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)`, border: `1px solid ${colors.border}` }}>
              <p className="text-[13px] font-semibold uppercase tracking-wide" style={{ color: colors.secondaryText }}>Download & Share</p>
              <div className="flex gap-2">
                <button onClick={downloadPDF}
                  className="flex-1 flex items-center justify-center gap-1.5 py-1 rounded-lg text-[13px] font-semibold text-white"
                  style={{ backgroundColor: '#dc2626' }}>
                  <Download className="w-3.5 h-3.5" /> Download PDF
                </button>
                <button onClick={sendEmail} disabled={sending}
                  className="flex-1 flex items-center justify-center gap-1.5 py-1 rounded-lg text-[13px] font-semibold text-white disabled:opacity-50"
                  style={{ backgroundColor: '#059669' }}>
                  <Send className="w-3.5 h-3.5" /> {sending ? 'Sending…' : 'Email Me'}
                </button>
              </div>
            </div>

            {/* Report preview */}
            <div>
              {/* Summary */}
              <AccordionSection id="summary" title="📊 Summary">
                <div className="grid grid-cols-3 gap-2 mb-3">
                  {[
                    { label: 'Total Orders', value: report.summary.total_orders, color: colors.primaryText },
                    { label: 'Executed', value: report.summary.executed_orders, color: '#16a34a' },
                    { label: 'Cancelled', value: report.summary.cancelled_orders, color: '#ef4444' },
                    { label: 'Closed Trades', value: report.summary.closed_trades, color: colors.primaryText },
                    { label: 'Win Rate', value: `${report.summary.win_rate}%`, color: report.summary.win_rate >= 50 ? '#16a34a' : '#ef4444' },
                    { label: 'Total P&L', value: formatPrice(report.summary.total_pnl), color: pnlColor(report.summary.total_pnl) },
                  ].map(({ label, value, color }) => (
                    <div key={label} className="stat-card rounded-lg p-1.5 text-center" style={{ backgroundColor: colors.inputBg }}>
                      <p className="stat-label text-[12px] mb-0.5" style={{ color: colors.secondaryText }}>{label}</p>
                      <p className="stat-value text-[14px] font-bold" style={{ color }}>{value}</p>
                    </div>
                  ))}
                </div>
                {(report.summary.best_trade || report.summary.worst_trade) && (
                  <div className="grid grid-cols-2 gap-2">
                    {report.summary.best_trade && (
                      <div className="rounded-lg p-1.5" style={{ backgroundColor: '#16a34a15', border: '1px solid #16a34a30' }}>
                        <p className="text-[12px] font-semibold mb-1" style={{ color: '#16a34a' }}>BEST TRADE</p>
                        <p className="text-[13px] font-bold" style={{ color: colors.primaryText }}>{report.summary.best_trade.symbol?.replace('.NS','')}</p>
                        <p className="text-[13px]" style={{ color: '#16a34a' }}>{formatPrice(report.summary.best_trade.pnl)} ({Number(report.summary.best_trade.pnl_pct || 0).toFixed(2)}%)</p>
                      </div>
                    )}
                    {report.summary.worst_trade && (
                      <div className="rounded-lg p-1.5" style={{ backgroundColor: '#ef444415', border: '1px solid #ef444430' }}>
                        <p className="text-[12px] font-semibold mb-1" style={{ color: '#ef4444' }}>WORST TRADE</p>
                        <p className="text-[13px] font-bold" style={{ color: colors.primaryText }}>{report.summary.worst_trade.symbol?.replace('.NS','')}</p>
                        <p className="text-[13px]" style={{ color: '#ef4444' }}>{formatPrice(report.summary.worst_trade.pnl)} ({Number(report.summary.worst_trade.pnl_pct || 0).toFixed(2)}%)</p>
                      </div>
                    )}
                  </div>
                )}
              </AccordionSection>

              {/* Analytics */}
              {report.analytics && (
                <AccordionSection id="analytics" title="📐 Risk & Quality Analytics">
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    {[
                      { label: 'Avg R:R Ratio', value: report.analytics.avg_rr ? `1:${report.analytics.avg_rr}` : 'N/A', color: report.analytics.avg_rr >= 2 ? '#16a34a' : report.analytics.avg_rr >= 1 ? '#f59e0b' : colors.secondaryText },
                      { label: 'SL Usage', value: `${report.analytics.sl_usage_pct}%`, color: report.analytics.sl_usage_pct >= 80 ? '#16a34a' : '#f59e0b' },
                      { label: 'Target Set', value: `${report.analytics.target_usage_pct}%`, color: report.analytics.target_usage_pct >= 60 ? '#16a34a' : '#f59e0b' },
                      { label: 'Max Win Streak', value: report.analytics.max_win_streak, color: '#16a34a' },
                      { label: 'Max Loss Streak', value: report.analytics.max_loss_streak, color: '#ef4444' },
                      { label: 'Avg Hold Time', value: report.analytics.avg_hold_hours > 24 ? `${Math.round(report.analytics.avg_hold_hours/24)}d` : `${report.analytics.avg_hold_hours}h`, color: colors.primaryText },
                    ].map(({ label, value, color }) => (
                      <div key={label} className="rounded-lg p-1.5 text-center" style={{ backgroundColor: colors.inputBg }}>
                        <p className="text-[12px] mb-0.5" style={{ color: colors.secondaryText }}>{label}</p>
                        <p className="text-[14px] font-bold" style={{ color }}>{value}</p>
                      </div>
                    ))}
                  </div>
                  <div className="space-y-1.5">
                    {[
                      { label: 'Max Drawdown', value: formatPrice(report.analytics.max_drawdown), color: '#ef4444' },
                      { label: 'Current Streak', value: report.analytics.current_streak > 0 ? `+${report.analytics.current_streak} wins` : report.analytics.current_streak < 0 ? `${Math.abs(report.analytics.current_streak)} losses` : 'None', color: report.analytics.current_streak > 0 ? '#16a34a' : '#ef4444' },
                    ].map(({ label, value, color }) => (
                      <div key={label} className="flex items-center justify-between px-3 py-1 rounded-lg" style={{ backgroundColor: colors.inputBg }}>
                        <span className="text-[13px]" style={{ color: colors.secondaryText }}>{label}</span>
                        <span className="text-[13px] font-bold" style={{ color }}>{value}</span>
                      </div>
                    ))}
                  </div>
                </AccordionSection>
              )}

              {/* Discipline */}
              {report.discipline?.score != null && (
                <AccordionSection id="discipline" title={`🏅 Discipline Score — ${report.discipline.score}/100`}>
                  <div className="mb-3">
                    <div className="h-3 rounded-full mb-1" style={{ backgroundColor: colors.inputBg }}>
                      <div className="h-3 rounded-full transition-all" style={{ width: `${report.discipline.score}%`, backgroundColor: report.discipline.score >= 70 ? '#16a34a' : report.discipline.score >= 50 ? '#f59e0b' : '#ef4444' }} />
                    </div>
                    <p className="text-[13px] text-right" style={{ color: colors.secondaryText }}>{report.discipline.score}/100</p>
                  </div>
                  <div className="space-y-2">
                    {Object.entries(report.discipline.components || {}).map(([k, v]) => {
                      const labels = { stop_loss:'Stop Loss', risk_reward:'Risk-Reward', strategy:'Strategy Consistency', emotion:'Emotional Control', frequency:'Trade Frequency', confidence:'Confidence', journal:'Journal', sl_violation:'SL Violations', risk_profile:'Risk Profile' };
                      const barColor = v >= 70 ? '#16a34a' : v >= 50 ? '#f59e0b' : '#ef4444';
                      return (
                        <div key={k}>
                          <div className="flex items-center justify-between mb-0.5">
                            <span className="text-[13px]" style={{ color: colors.secondaryText }}>{labels[k] || k}</span>
                            <span className="text-[13px] font-bold" style={{ color: barColor }}>{v}%</span>
                          </div>
                          <div className="h-1.5 rounded-full" style={{ backgroundColor: colors.inputBg }}>
                            <div className="h-1.5 rounded-full" style={{ width: `${v}%`, backgroundColor: barColor }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </AccordionSection>
              )}

              {/* Top/Bottom Performers */}
              {(report.top_performers?.length > 0 || report.bottom_performers?.length > 0) && (
                <AccordionSection id="performers" title="🏆 Top & Bottom Performers">
                  <div className="grid grid-cols-2 gap-3">
                    {report.top_performers?.length > 0 && (
                      <div>
                        <p className="text-[12px] font-semibold uppercase mb-2" style={{ color: '#16a34a' }}>Top Stocks</p>
                        <div className="space-y-1">
                          {report.top_performers.map((p, i) => (
                            <div key={i} className="flex items-center justify-between px-3 py-1 rounded-lg" style={{ backgroundColor: '#16a34a10' }}>
                              <span className="text-[13px] font-semibold" style={{ color: colors.primaryText }}>{p.symbol}</span>
                              <span className="text-[13px] font-bold" style={{ color: '#16a34a' }}>+{formatPrice(p.pnl)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {report.bottom_performers?.length > 0 && (
                      <div>
                        <p className="text-[12px] font-semibold uppercase mb-2" style={{ color: '#ef4444' }}>Bottom Stocks</p>
                        <div className="space-y-1">
                          {report.bottom_performers.map((p, i) => (
                            <div key={i} className="flex items-center justify-between px-3 py-1 rounded-lg" style={{ backgroundColor: '#ef444410' }}>
                              <span className="text-[13px] font-semibold" style={{ color: colors.primaryText }}>{p.symbol}</span>
                              <span className="text-[13px] font-bold" style={{ color: '#ef4444' }}>{formatPrice(p.pnl)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </AccordionSection>
              )}

              {/* By Source */}
              {Object.keys(report.by_source).length > 0 && (
                <AccordionSection id="source" title="🏷 Orders by Source">
                  <div className="grid grid-cols-3 gap-2">
                    {Object.entries(report.by_source).map(([src, cnt]) => (
                      <div key={src} className="rounded-lg p-1.5 text-center" style={{ backgroundColor: colors.inputBg }}>
                        <p className="text-[13px] font-bold mb-1" style={{ color: srcColor(src) }}>{srcLabel(src)}</p>
                        <p className="text-[17px] font-black" style={{ color: colors.primaryText }}>{cnt}</p>
                        <p className="text-[12px]" style={{ color: colors.secondaryText }}>orders</p>
                      </div>
                    ))}
                  </div>
                </AccordionSection>
              )}

              {/* By Product Type */}
              {Object.keys(report.by_product_type).length > 0 && (
                <AccordionSection id="product" title="📈 Intraday vs Swing">
                  <div className="space-y-2">
                    {Object.entries(report.by_product_type).map(([pt, data]) => {
                      const wr = data.trades ? Math.round(data.wins/data.trades*100) : 0;
                      const ptColor = pt === 'INTRADAY' ? '#7c3aed' : colors.accent;
                      return (
                        <div key={pt} className="rounded-lg p-1.5" style={{ backgroundColor: colors.inputBg, borderLeft: `3px solid ${ptColor}` }}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-[13px] font-bold" style={{ color: ptColor }}>{pt}</span>
                            <span className="text-[14px] font-bold" style={{ color: pnlColor(data.pnl) }}>{formatPrice(data.pnl)}</span>
                          </div>
                          <div className="flex gap-4 text-[13px]" style={{ color: colors.secondaryText }}>
                            <span>{data.trades} trades</span>
                            <span style={{ color: '#16a34a' }}>{data.wins} wins</span>
                            <span style={{ color: wr >= 50 ? '#16a34a' : '#ef4444' }}>{wr}% WR</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </AccordionSection>
              )}

              {/* By Strategy */}
              {Object.keys(report.by_strategy).length > 0 && (
                <AccordionSection id="strategy" title="⚙ Strategy Breakdown">
                  <div className="space-y-1.5">
                    {Object.entries(report.by_strategy).sort((a,b) => b[1].trades - a[1].trades).map(([tag, data]) => (
                      <div key={tag} className="flex items-center justify-between px-3 py-1 rounded-lg" style={{ backgroundColor: colors.inputBg }}>
                        <span className="text-[13px] px-1.5 py-1 rounded-full font-medium"
                          style={{ backgroundColor: colors.accent + '20', color: colors.accent }}>{tag}</span>
                        <div className="flex items-center gap-3 text-[13px]">
                          <span style={{ color: colors.secondaryText }}>{data.trades} trades</span>
                          <span className="font-semibold" style={{ color: pnlColor(data.pnl) }}>{formatPrice(data.pnl)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </AccordionSection>
              )}

              {/* All Orders */}
              <AccordionSection id="orders" title={`📋 All Orders (${report.orders.length})`}>
                <div className="space-y-1.5 max-h-80 overflow-y-auto">
                  {report.orders.map((o, i) => {
                    const sym = (o.symbol || '').replace('.NS', '');
                    const price = o.executed_price || o.limit_price || 0;
                    const isBuy = o.order_type === 'BUY';
                    return (
                      <div key={i} className="flex items-center justify-between px-3 py-1 rounded-lg" style={{ backgroundColor: colors.inputBg }}>
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="w-5 h-5 rounded flex items-center justify-center text-[12px] font-bold flex-shrink-0"
                            style={{ backgroundColor: isBuy ? '#16a34a22' : '#dc262622', color: isBuy ? '#16a34a' : '#dc2626' }}>
                            {isBuy ? 'B' : 'S'}
                          </span>
                          <div className="min-w-0">
                            <p className="text-[13px] font-semibold leading-tight" style={{ color: colors.primaryText }}>{sym}</p>
                            <p className="text-[12px] truncate" style={{ color: colors.secondaryText }}>
                              {o.quantity} × {formatPrice(price)} · {o.order_variant}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className="text-[12px] font-medium" style={{ color: srcColor(o.source) }}>{srcLabel(o.source)}</span>
                          <span className="text-[12px] font-semibold" style={{ color: statusColor(o.status) }}>{o.status}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </AccordionSection>

              {/* Closed Trades */}
              {report.closed_trades.length > 0 && (
                <AccordionSection id="trades" title={`💰 Closed Trades (${report.closed_trades.length})`}>
                  <div className="space-y-2 max-h-[600px] overflow-y-auto">
                    {report.closed_trades.map((t, i) => {
                      const sym = (t.symbol || '').replace('.NS', '');
                      const disc = t.discipline_snap || {};
                      const mkt  = t.market_context_snap || {};
                      const sym_ctx = t.symbol_context_snap || {};
                      const hasAnalysis = t.ai_review || disc.discipline_score != null || mkt.regime;
                      const discScore = disc.discipline_score ?? disc.score ?? null;
                      const discColor = discScore >= 70 ? '#16a34a' : discScore >= 50 ? '#f59e0b' : '#ef4444';
                      const actualR = t.actual_r != null ? Number(t.actual_r).toFixed(2) : null;
                      const tags = t.strategy_tags || [];

                      return (
                        <div key={i} className="rounded-lg overflow-hidden"
                          style={{ backgroundColor: colors.inputBg, borderLeft: `2px solid ${pnlColor(t.pnl)}` }}>
                          {/* Top row */}
                          <div className="flex items-center justify-between px-3 py-2">
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="text-[13px] font-semibold" style={{ color: colors.primaryText }}>{sym}</p>
                                <span className="text-[11px] px-1.5 py-0.5 rounded" style={{ backgroundColor: colors.accent + '20', color: colors.accent }}>{t.product_type}</span>
                                {t.exit_reason && <span className="text-[11px]" style={{ color: colors.secondaryText }}>{t.exit_reason}</span>}
                              </div>
                              <p className="text-[12px] mt-0.5" style={{ color: colors.secondaryText }}>
                                Entry {formatPrice(t.entry_price)} → Exit {formatPrice(t.exit_price)}
                                {t.stop_loss ? ` · SL ${formatPrice(t.stop_loss)}` : ''}
                                {t.target_price ? ` · Tgt ${formatPrice(t.target_price)}` : ''}
                              </p>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <p className="text-[13px] font-bold" style={{ color: pnlColor(t.pnl) }}>
                                {Number(t.pnl) >= 0 ? '+' : ''}{formatPrice(t.pnl)}
                              </p>
                              <p className="text-[12px]" style={{ color: pnlColor(t.pnl) }}>
                                {Number(t.pnl_pct || 0) >= 0 ? '+' : ''}{Number(t.pnl_pct || 0).toFixed(2)}%
                                {actualR ? ` · R: ${actualR}` : ''}
                              </p>
                            </div>
                          </div>

                          {/* Tags row */}
                          {tags.length > 0 && (
                            <div className="flex flex-wrap gap-1 px-3 pb-1.5">
                              {tags.map(tag => (
                                <span key={tag} className="text-[11px] px-1.5 py-0.5 rounded-full"
                                  style={{ backgroundColor: colors.accent + '15', color: colors.accent }}>{tag}</span>
                              ))}
                            </div>
                          )}

                          {/* Analysis row */}
                          {hasAnalysis && (
                            <div className="border-t px-3 py-2 space-y-1.5" style={{ borderColor: colors.border }}>
                              {/* Discipline snap */}
                              {discScore != null && (
                                <div className="flex items-center gap-2">
                                  <span className="text-[12px]" style={{ color: colors.secondaryText }}>Discipline</span>
                                  <div className="flex-1 h-1.5 rounded-full" style={{ backgroundColor: colors.border }}>
                                    <div className="h-1.5 rounded-full" style={{ width: `${discScore}%`, backgroundColor: discColor }} />
                                  </div>
                                  <span className="text-[12px] font-bold" style={{ color: discColor }}>{discScore}/100</span>
                                </div>
                              )}
                              {/* Discipline snap checklist */}
                              {(disc.sl_placed != null || disc.tagged != null || disc.journaled != null || disc.planned_exit != null) && (
                                <div className="flex flex-wrap gap-2">
                                  {[
                                    { label: 'SL Set', val: disc.sl_placed },
                                    { label: 'Tagged', val: disc.tagged },
                                    { label: 'Journaled', val: disc.journaled },
                                    { label: 'Planned Exit', val: disc.planned_exit },
                                  ].filter(d => d.val != null).map(({ label, val }) => (
                                    <span key={label} className="text-[11px] flex items-center gap-0.5"
                                      style={{ color: val ? '#16a34a' : '#ef4444' }}>
                                      {val ? '✓' : '✗'} {label}
                                    </span>
                                  ))}
                                </div>
                              )}
                              {/* Market context */}
                              {mkt.regime && (
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-[11px] px-1.5 py-0.5 rounded font-medium"
                                    style={{ backgroundColor: '#6366f120', color: '#818cf8' }}>{mkt.regime}</span>
                                  {mkt.fii_net != null && (
                                    <span className="text-[11px]" style={{ color: Number(mkt.fii_net) >= 0 ? '#16a34a' : '#ef4444' }}>
                                      FII {Number(mkt.fii_net) >= 0 ? '+' : ''}{formatNumber(mkt.fii_net)}Cr
                                    </span>
                                  )}
                                  {mkt.dii_net != null && (
                                    <span className="text-[11px]" style={{ color: Number(mkt.dii_net) >= 0 ? '#16a34a' : '#ef4444' }}>
                                      DII {Number(mkt.dii_net) >= 0 ? '+' : ''}{formatNumber(mkt.dii_net)}Cr
                                    </span>
                                  )}
                                </div>
                              )}
                              {/* Scanner signal */}
                              {sym_ctx.signal && (
                                <p className="text-[12px]" style={{ color: colors.secondaryText }}>
                                  Signal: <span style={{ color: colors.primaryText }}>{sym_ctx.signal}</span>
                                  {sym_ctx.scanner && ` · ${sym_ctx.scanner}`}
                                </p>
                              )}
                              {/* AI review */}
                              {t.ai_review && (
                                <p className="text-[12px] italic leading-relaxed" style={{ color: colors.secondaryText }}>
                                  "{t.ai_review.slice(0, 180)}{t.ai_review.length > 180 ? '…' : ''}"
                                </p>
                              )}
                              {/* Charges */}
                              {t.total_charges != null && Number(t.total_charges) > 0 && (
                                <p className="text-[11px]" style={{ color: colors.secondaryText }}>
                                  Charges: <span style={{ color: '#ef4444' }}>₹{Number(t.total_charges).toFixed(2)}</span>
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </AccordionSection>
              )}
            </div>

            {/* Footer note */}
            <p className="text-[13px] text-center pb-1" style={{ color: colors.secondaryText + '80' }}>
              Report generated {report.meta.generated_at?.slice(0,19).replace('T',' ')} IST
            </p>
          </>
        )}
      </div>
    );
}
